import { useEffect, useRef } from 'react';
import { routeStyle, routeHaloStyle, routeRenderLayer } from '../../lib/designApi';

// Shape-distinct on-map symbology matched to the real Irancell KMZ:
// OLT = green diamond, ODC = cyan house, FAT = blue triangle (M bigger than S),
// HH = navy square. Each is a google.maps.Symbol (SVG path) so the shapes read
// at a glance. Scales are kept small so the markers never bury the cable lines.
const MARKER_SHAPES = {
  olt: { path: 'M 0,-9 L 9,0 L 0,9 L -9,0 Z', fill: '#16a34a', scale: 0.95, zIndex: 14 },
  odc: { path: 'M -7,2 L -7,8 L 7,8 L 7,2 L 0,-8 Z', fill: '#06b6d4', scale: 0.72, zIndex: 12 },
  // round-11 team ask: MFAT = a TRIANGLE like the FAT but PINK (was a cyan cabinet).
  // 'fat' = the single leaf (blue triangle). Legacy mfat/sfat KEPT so old designs render.
  mfat_l2: { path: 'M 0,-9 L 8,7 L -8,7 Z', fill: '#ec4899', scale: 0.6, zIndex: 12 },
  fat: { path: 'M 0,-9 L 8,7 L -8,7 Z', fill: '#3b82f6', scale: 0.46, zIndex: 10 },
  mfat: { path: 'M 0,-9 L 8,7 L -8,7 Z', fill: '#2563eb', scale: 0.6, zIndex: 11 },
  sfat: { path: 'M 0,-9 L 8,7 L -8,7 Z', fill: '#3b82f6', scale: 0.46, zIndex: 10 },
  hh: { path: 'M -7,-7 L 7,-7 L 7,7 L -7,7 Z', fill: '#1e3a8a', scale: 0.44, zIndex: 9 },
  // T-connector: a single inline tap on the trench (amber hexagon).
  tconnector: { path: 'M 0,-7 L 6,-3.5 L 6,3.5 L 0,7 L -6,3.5 L -6,-3.5 Z', fill: '#f59e0b', scale: 0.5, zIndex: 13 },
  // H-connector: a shared branch hub where >=2 FATs splice (magenta circle) -
  // distinct shape+colour from the T so the junction type reads at a glance.
  hconnector: { path: 'M 0,-7 C 3.9,-7 7,-3.9 7,0 C 7,3.9 3.9,7 0,7 C -3.9,7 -7,3.9 -7,0 C -7,-3.9 -3.9,-7 0,-7 Z', fill: '#a21caf', scale: 0.55, zIndex: 13 },
};

// round-11 B7: a device the engine left «بدونِ مسیر» (no ground path - the round-10
// footprint-locked data class) renders GREY with a red outline instead of looking
// like a healthy connected box. Detection = the per-element rationale check the
// engine already attaches.
function isRouteless(e) {
  const checks = (e.attrs && e.attrs.rationale && e.attrs.rationale.checks) || [];
  return checks.some((c) => c.key === 'odc_ground_distance'
    && c.status === 'fail' && c.actual === 'بدونِ مسیر');
}

function markerIcon(type, routeless = false) {
  const shape = MARKER_SHAPES[type] || MARKER_SHAPES.hh;
  return {
    path: shape.path,
    anchor: new window.google.maps.Point(0, 0),
    scale: shape.scale,
    fillColor: routeless ? '#9ca3af' : shape.fill,
    fillOpacity: routeless ? 0.85 : 1,
    strokeColor: routeless ? '#b91c1c' : '#0b1220',
    strokeWeight: routeless ? 1.5 : 1,
  };
}

// Draws a design result (elements + routes) on the Google map. Each route is
// painted twice: a dark halo underneath, then the colour line on top, so the
// cabling stays legible over the roadmap. The full symbology legend lives in the
// "راهنمای نمادها (KMZ)" modal, so nothing is rendered as a permanent overlay.
export function DesignLayer({ map, result, detailed = false, onHover, onSelect, editMode = false, onElementMove, onElementSelect }) {
  const drawn = useRef([]);

  useEffect(() => {
    if (!map) return undefined;
    drawn.current.forEach((o) => { if (o.setMap) o.setMap(null); });
    drawn.current = [];
    if (!result) return undefined;

    // The MFAT feed renders like the FAT line (purple), not feeder-yellow, while a drop
    // from an MFAT stays a drop - the same routeRenderLayer as the KMZ export.
    const mfatIds = new Set((result.elements || []).filter((e) => e.type === 'mfat_l2').map((e) => e.ext_id));
    (result.routes || []).forEach((r) => {
      const line = (r.line || r.geometry?.coordinates || []).map(([lng, lat]) => ({ lat, lng }));
      if (line.length < 2) return;
      const lay = routeRenderLayer(r, mfatIds);
      const halo = new window.google.maps.Polyline({
        path: line, clickable: false, zIndex: 3, map, ...routeHaloStyle(lay),
      });
      const poly = new window.google.maps.Polyline({
        path: line, clickable: false, zIndex: 4, map, ...routeStyle(lay),
      });
      drawn.current.push(halo, poly);
    });

    (result.elements || []).forEach((e) => {
      const coords = e.point || e.geometry?.coordinates;
      if (!coords) return;
      const [lng, lat] = coords;
      const shape = MARKER_SHAPES[e.type] || MARKER_SHAPES.hh;
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        icon: markerIcon(e.type, isRouteless(e)),
        clickable: detailed || editMode,
        draggable: editMode,
        zIndex: shape.zIndex,
        map,
      });
      if (editMode) {
        // Edit mode: drag to reposition (commits to the in-memory result on dragend),
        // click to open the element card (where «حذف عنصر» lives).
        const info = { kind: 'element', element: e };
        if (onElementMove) {
          marker.addListener('dragend', (ev) => onElementMove(e.ext_id ?? e.id, [ev.latLng.lng(), ev.latLng.lat()]));
        }
        if (onElementSelect) marker.addListener('click', () => onElementSelect(info));
      } else if (detailed) {
        const info = { kind: 'element', element: e };
        // hover PREVIEWS the card; click LOCKS it so the cursor can reach the
        // card's buttons without the content changing (the card's × unlocks).
        if (onHover) marker.addListener('mouseover', () => onHover(info));
        if (onSelect) marker.addListener('click', () => onSelect(info));
      }
      drawn.current.push(marker);
    });

    return () => { drawn.current.forEach((o) => { if (o.setMap) o.setMap(null); }); drawn.current = []; };
  }, [map, result, detailed, onHover, onSelect, editMode, onElementMove, onElementSelect]);

  return null;
}
